import { FC, useState } from 'react';
import { SocialConnectionCard } from './SocialConnectionCard';
import { useSocialConnections } from './useSocialConnections';
import { LinkedInCompanyPageSelector } from '@/features/marketing/components';

interface SocialConnectionsPanelProps {
    showLinkedIn?: boolean;
    showFacebook?: boolean;
    showInstagram?: boolean;
    showTwitter?: boolean;
    showLinkedInPageSelector?: boolean;
    compact?: boolean;
    onLinkedInPageSelect?: (urn: string, url: string) => void;
}

/**
 * SocialConnectionsPanel - Container for all social platform connections
 * Follows single responsibility by delegating to individual card components
 */
export const SocialConnectionsPanel: FC<SocialConnectionsPanelProps> = ({
    showLinkedIn = true,
    showFacebook = true,
    showInstagram = true,
    showTwitter = true,
    showLinkedInPageSelector = true,
    compact = false,
    onLinkedInPageSelect,
}) => {
    const {
        linkedInConnected,
        facebookConnected,
        twitterConnected,
        isLoading,
        isConnecting,
        accountsLoading,
        facebookPages,
        facebookSelectedPageName,
        instagramAccounts,
        selectedInstagramAccount,
        twitterUsername,
        connectLinkedIn,
        connectFacebookInstagram,
        connectTwitter,
        disconnectLinkedIn,
        disconnectFacebookInstagram,
        disconnectTwitter,
        selectFacebookPage,
        selectInstagramAccount,
    } = useSocialConnections();

    const [selectedLinkedInUrn, setSelectedLinkedInUrn] = useState<string | undefined>();

    if (isLoading) {
        return (
            <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
                ))}
            </div>
        );
    }

    return (
        <div className={`space-y-${compact ? '2' : '4'}`}>
            {/* LinkedIn */}
            {showLinkedIn && (
                <div className="space-y-2">
                    <SocialConnectionCard
                        platform="linkedin"
                        isConnected={linkedInConnected}
                        isLoading={isConnecting === 'linkedin'}
                        onConnect={() => connectLinkedIn()}
                        onDisconnect={disconnectLinkedIn}
                    />

                    {/* LinkedIn Company Page Selector */}
                    {linkedInConnected && showLinkedInPageSelector && (
                        <div className="ml-8 mt-2">
                            <LinkedInCompanyPageSelector
                                selectedUrn={selectedLinkedInUrn}
                                onSelect={(urn, url) => {
                                    setSelectedLinkedInUrn(urn);
                                    if (onLinkedInPageSelect && urn && url) {
                                        onLinkedInPageSelect(urn, url);
                                    }
                                }}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Twitter */}
            {showTwitter && (
                <SocialConnectionCard
                    platform="twitter"
                    isConnected={twitterConnected}
                    isLoading={isConnecting === 'twitter'}
                    connectedAccountName={twitterUsername || undefined}
                    onConnect={() => connectTwitter()}
                    onDisconnect={disconnectTwitter}
                />
            )}

            {/* Meta (Facebook & Instagram) - Unified when disconnected */}
            {showFacebook && showInstagram && !facebookConnected && (
                <SocialConnectionCard
                    platform="meta"
                    isConnected={false}
                    isLoading={isConnecting === 'facebook'}
                    onConnect={() => connectFacebookInstagram()}
                    onDisconnect={disconnectFacebookInstagram}
                />
            )}

            {/* Meta Connected State - Professional Minimal Design */}
            {showFacebook && showInstagram && facebookConnected && (
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                    {/* Header */}
                    <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                </svg>
                            </div>
                            <div>
                                <div className="font-semibold text-gray-900 text-sm">Meta Platforms</div>
                                <div className="text-xs text-gray-500">Facebook & Instagram</div>
                            </div>
                        </div>
                        <button
                            onClick={disconnectFacebookInstagram}
                            disabled={isConnecting === 'facebook'}
                            className="text-sm text-gray-600 hover:text-red-600 font-medium transition-colors disabled:opacity-50"
                        >
                            Disconnect
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-5 space-y-5">
                        {/* Facebook Page Selection */}
                        {facebookPages.length > 0 && (
                            <div>
                                <div className="text-sm font-medium text-gray-700 mb-2.5">Facebook Page</div>
                                <div className="space-y-1.5">
                                    {facebookPages.map((page) => (
                                        <label
                                            key={page.pageId}
                                            className={`flex items-center gap-3 px-4 py-3 rounded-md border cursor-pointer transition-all ${facebookSelectedPageName === page.pageName
                                                ? 'border-blue-600 bg-blue-50/50'
                                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                                }`}
                                        >
                                            <input
                                                type="radio"
                                                name="facebook-page"
                                                checked={facebookSelectedPageName === page.pageName}
                                                onChange={() => selectFacebookPage(page)}
                                                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium text-gray-900 text-sm">{page.pageName}</div>
                                                {page.category && <div className="text-xs text-gray-500 mt-0.5">{page.category}</div>}
                                            </div>
                                            {facebookSelectedPageName === page.pageName && (
                                                <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Instagram Account Selection */}
                        {instagramAccounts.length > 0 && (
                            <div>
                                <div className="text-sm font-medium text-gray-700 mb-2.5">Instagram Account</div>
                                <div className="space-y-1.5">
                                    {instagramAccounts.map((account) => (
                                        <label
                                            key={account.instagramAccountId}
                                            className={`flex items-center gap-3 px-4 py-3 rounded-md border cursor-pointer transition-all ${selectedInstagramAccount?.instagramAccountId === account.instagramAccountId
                                                ? 'border-purple-600 bg-purple-50/50'
                                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                                }`}
                                        >
                                            <input
                                                type="radio"
                                                name="instagram-account"
                                                checked={selectedInstagramAccount?.instagramAccountId === account.instagramAccountId}
                                                onChange={() => selectInstagramAccount(account)}
                                                className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium text-gray-900 text-sm">@{account.username}</div>
                                                <div className="text-xs text-gray-500 mt-0.5">via {account.linkedPageName}</div>
                                            </div>
                                            {selectedInstagramAccount?.instagramAccountId === account.instagramAccountId && (
                                                <svg className="w-4 h-4 text-purple-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* No Instagram warning */}
                        {instagramAccounts.length === 0 && !accountsLoading && (
                            <div className="flex gap-3 p-3.5 bg-amber-50 border border-amber-200 rounded-md">
                                <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <div className="flex-1 text-sm">
                                    <div className="font-medium text-amber-900">No Instagram Business accounts found</div>
                                    <div className="text-amber-700 text-xs mt-0.5">Connect an Instagram Business/Creator account to your Facebook Page to enable posting.</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Info note */}
            <div className="flex gap-2 text-xs text-gray-500 mt-3">
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span>Facebook and Instagram use the same Meta login. Instagram requires a Business/Creator account linked to a Facebook Page.</span>
            </div>
        </div>
    );
};
